import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  Search,
  Phone,
  ArrowRight,
  Clock,
  DollarSign,
  CheckCircle,
  Activity,
  Calculator,
  MapPin,
  AlertTriangle,
  Shield,
  Heart,
  Pill
} from "lucide-react";
import { Header, Footer } from "../../../components/Layout";

const DesintoxicacionPage = () => {
  const phases = [
    {
      title: "Evaluación Inicial",
      duration: "Primeras horas",
      description: "Exámenes médicos, evaluación del estado de intoxicación y planificación del protocolo de desintoxicación."
    },
    {
      title: "Estabilización",
      duration: "1-3 días",
      description: "Manejo de los síntomas agudos de abstinencia con supervisión médica 24/7 y medicación de apoyo."
    },
    {
      title: "Desintoxicación Activa",
      duration: "4-14 días",
      description: "Eliminación progresiva de la sustancia del cuerpo. Tratamiento de síntomas físicos y psicológicos."
    },
    {
      title: "Transición",
      duration: "Últimos días",
      description: "Preparación para el tratamiento de rehabilitación. Evaluación y derivación a programa adecuado."
    }
  ];

  const substanceProtocols = [
    { substance: "Alcohol", duration: "7-10 días", risk: "Alto", notes: "Riesgo de convulsiones, requiere supervisión médica estricta" },
    { substance: "Benzodiazepinas", duration: "14-21 días", risk: "Alto", notes: "Desintoxicación gradual obligatoria, riesgo de convulsiones" },
    { substance: "Opioides", duration: "5-7 días", risk: "Moderado", notes: "Síntomas muy intensos pero menor riesgo vital" },
    { substance: "Cocaína/Estimulantes", duration: "3-7 días", risk: "Moderado", notes: "Principalmente síntomas psicológicos: depresión, fatiga" },
    { substance: "Cannabis", duration: "7-14 días", risk: "Bajo", notes: "Síntomas leves, generalmente no requiere hospitalización" }
  ];

  const warnings = [
    "NUNCA intentes desintoxicarte solo de alcohol o benzodiacepinas - puede ser mortal",
    "Los síntomas de abstinencia pueden aparecer horas después del último consumo",
    "La desintoxicación es solo el primer paso, no es tratamiento completo"
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    "name": "Desintoxicación para Adicciones",
    "description": "Fase inicial de tratamiento para eliminar sustancias del cuerpo de forma segura y supervisada médicamente."
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Tratamientos", "item": "https://sinadicciones.org/tratamientos" },
      { "@type": "ListItem", "position": 3, "name": "Desintoxicación", "item": "https://sinadicciones.org/tratamiento/desintoxicacion" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Desintoxicación para Adicciones: Tratamiento Médico Supervisado | Sin Adicciones Chile</title>
        <meta name="description" content="Desintoxicación supervisada para adicciones en Chile. Manejo seguro del síndrome de abstinencia. Desde $800.000. Protocolos para alcohol, drogas y medicamentos." />
        <link rel="canonical" href="https://sinadicciones.org/tratamiento/desintoxicacion" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/tratamiento/desintoxicacion" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      <Header />
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/tratamientos" className="hover:text-emerald-400 transition-colors">
              Tratamientos
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Desintoxicación</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-red-500/20 flex items-center justify-center">
                <Activity className="w-7 h-7 text-red-400" />
              </div>
              <div className="px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-full">
                <span className="text-red-400 font-medium">Supervisión médica 24/7</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400">
                Desintoxicación
              </span>
              {" "}Supervisada
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl">
              La fase inicial y crítica del tratamiento. Elimina las sustancias 
              de tu cuerpo de forma segura, con supervisión médica profesional 
              y manejo del síndrome de abstinencia.
            </p>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <DollarSign className="w-5 h-5 text-red-400 mb-2" />
                <p className="text-white font-bold">$800.000 - $2M</p>
                <p className="text-slate-400 text-sm">costo total</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Clock className="w-5 h-5 text-red-400 mb-2" />
                <p className="text-white font-bold">7-21 días</p>
                <p className="text-slate-400 text-sm">duración</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Shield className="w-5 h-5 text-red-400 mb-2" />
                <p className="text-white font-bold">24/7</p>
                <p className="text-slate-400 text-sm">supervisión</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Pill className="w-5 h-5 text-red-400 mb-2" />
                <p className="text-white font-bold">Médico</p>
                <p className="text-slate-400 text-sm">enfoque</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile?service=desintoxicacion"
                className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Centros con Desintoxicación
              </Link>
              <Link 
                to="/calculadora"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Calculator className="w-5 h-5" />
                Calcular Costo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Warning */}
      <section className="bg-red-500/10 border-y border-red-500/20 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-8 h-8 text-red-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-red-400 font-bold text-lg mb-2">Advertencias Importantes</h3>
              <ul className="space-y-2">
                {warnings.map((warning, index) => (
                  <li key={index} className="text-slate-300 flex items-start gap-2">
                    <span className="text-red-400">•</span>
                    {warning}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Phases */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Fases de la Desintoxicación
            </h2>
          </div>

          <div className="space-y-4">
            {phases.map((phase, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50"
              >
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-red-400 font-bold">{index + 1}</span>
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold text-white">{phase.title}</h3>
                    <span className="px-3 py-1 bg-red-500/10 text-red-400 text-sm rounded-full">
                      {phase.duration}
                    </span>
                  </div>
                  <p className="text-slate-400">{phase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Protocols by Substance */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Protocolos por Sustancia
            </h2>
            <p className="text-slate-400">
              Cada sustancia tiene un protocolo de desintoxicación diferente.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-4 px-4 text-slate-300 font-semibold">Sustancia</th>
                  <th className="text-left py-4 px-4 text-slate-300 font-semibold">Duración</th>
                  <th className="text-left py-4 px-4 text-slate-300 font-semibold">Riesgo</th>
                  <th className="text-left py-4 px-4 text-slate-300 font-semibold">Notas</th>
                </tr>
              </thead>
              <tbody>
                {substanceProtocols.map((protocol, index) => (
                  <tr key={index} className="border-b border-slate-800">
                    <td className="py-4 px-4 text-white font-medium">{protocol.substance}</td>
                    <td className="py-4 px-4 text-slate-400">{protocol.duration}</td>
                    <td className="py-4 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        protocol.risk === 'Alto' 
                          ? 'bg-red-500/20 text-red-400' 
                          : protocol.risk === 'Moderado'
                          ? 'bg-orange-500/20 text-orange-400'
                          : 'bg-green-500/20 text-green-400'
                      }`}>
                        {protocol.risk}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-slate-400 text-sm">{protocol.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-emerald-900/30 to-slate-800/50 rounded-3xl p-8 border border-emerald-500/20">
            <div className="flex items-center gap-3 mb-4">
              <Heart className="w-8 h-8 text-emerald-400" />
              <h3 className="text-xl font-bold text-white">Después de la Desintoxicación</h3>
            </div>
            <p className="text-slate-300 mb-4">
              La desintoxicación es solo el <strong>primer paso</strong>. Elimina la dependencia física, 
              pero no trata las causas de la adicción. Para una recuperación duradera, es esencial 
              continuar con un programa de rehabilitación.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link 
                to="/tratamiento/residencial"
                className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-lg hover:bg-emerald-500/20 transition-all"
              >
                Tratamiento Residencial
              </Link>
              <Link 
                to="/tratamiento/ambulatorio"
                className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-lg hover:bg-emerald-500/20 transition-all"
              >
                Tratamiento Ambulatorio
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-red-900/50 to-slate-800/50 border border-red-500/20 p-8 md:p-12 text-center">
            <Activity className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              ¿Necesitas Desintoxicación Urgente?
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Encuentra centros que ofrecen desintoxicación supervisada con ingreso inmediato.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile"
                className="px-8 py-4 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl transition-all flex items-center gap-2"
              >
                <MapPin className="w-5 h-5" />
                Buscar Centros
              </Link>
              <a 
                href="tel:1412"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Fono Drogas: 1412
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-6">Otros Tipos de Tratamiento</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Ambulatorio", path: "/tratamiento/ambulatorio", icon: "🏢" },
              { title: "Residencial", path: "/tratamiento/residencial", icon: "🏥" },
              { title: "Online", path: "/tratamiento/online", icon: "💻" },
              { title: "Grupos de Apoyo", path: "/tratamiento/grupos-apoyo", icon: "👥" }
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-red-500/30 transition-all group"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-white group-hover:text-red-400 transition-colors">{item.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DesintoxicacionPage;
